/**
 * PUNCHES, LAID OUT THE WAY THE PAYROLL CALCULATOR READS THEM.
 *
 * Anthony, 2026-10-05, moved Nina, Jeremy and Ashley to W-2. The desk's pay
 * run multiplies hours by a rate and stops there. California wants daily
 * overtime, meal timing, premiums and a minimum-wage check, and the shop's
 * cross-check for that is a separate calculator (_ops/payroll/ca_payroll.py)
 * that takes either a flat CSV of shifts or the raw pay_clock rows as JSON.
 * This file turns the desk's clock punches into both shapes and nothing
 * else: no pay math, no decisions, no network.
 *
 * THE WORKDAY IS THE PACIFIC DAY THE SHIFT STARTED. The same rule as
 * clock_out() in migration 0191, so a shift that runs past midnight is one
 * row on the day it began, and the CSV writes its clock-out as a wall time
 * earlier than its clock-in. The calculator rolls that forward itself.
 *
 * TIMES ARE WALL CLOCK IN THE ZONE, NOT OFFSETS. 08:00 is 08:00 whether
 * daylight saving is on or off, which is why the formatting goes through
 * Intl with the zone rather than through Date arithmetic.
 *
 * WHAT IS LEFT OUT, AND SAID. A shift still on the clock has no end, so it
 * is not a row; the screen lists who. Rest breaks are paid and not punched,
 * so rest_breaks_taken is blank and the calculator flags them as
 * unrecorded rather than owed. The CSV has two meal slots; a third punched
 * break goes in the note so nothing disappears quietly.
 */

import { PACIFIC } from "./zoneclock.ts";

export type PunchBreak = { started_at: string; ended_at: string | null };

export type Punch = {
  id: string;
  technician_id: string;
  clock_in: string;
  clock_out: string | null;
  breaks: PunchBreak[];
};

/** One line of the calculator's CSV, in its column order. */
export type ExportRow = {
  employee: string;
  date: string;
  clock_in: string;
  clock_out: string;
  meal_start: string;
  meal_end: string;
  rest_breaks_taken: string;
  meal2_start: string;
  meal2_end: string;
  note: string;
};

export const CSV_COLUMNS: ReadonlyArray<keyof ExportRow> = [
  "employee", "date", "clock_in", "clock_out", "meal_start", "meal_end",
  "rest_breaks_taken", "meal2_start", "meal2_end", "note",
];

/** One row of the calculator's JSON, the pay_clock shape it documents. */
export type ExportPunch = {
  technician: string;
  clock_in: string;
  clock_out: string;
  breaks: Array<{ started_at: string; ended_at: string }>;
};

export type Export = {
  rows: ExportRow[];
  punches: ExportPunch[];
  /** Names of people whose shift is still open and so was left out. */
  stillOpen: string[];
};

type Names = ReadonlyMap<string, string> | Record<string, string>;
const nameOf = (names: Names, id: string): string =>
  (names instanceof Map ? names.get(id) : (names as Record<string, string>)[id]) ?? "Unknown";

/** "2026-10-05" and "08:00", wall clock in the zone. */
export function wallClock(iso: string, zone = PACIFIC): { date: string; time: string } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zone, hourCycle: "h23",
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
  }).formatToParts(new Date(iso));
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "00";
  // Some engines print midnight as "24" even under h23.
  const hour = get("hour") === "24" ? "00" : get("hour");
  return { date: `${get("year")}-${get("month")}-${get("day")}`, time: `${hour}:${get("minute")}` };
}

const byStart = (a: PunchBreak, b: PunchBreak) => Date.parse(a.started_at) - Date.parse(b.started_at);

/** Punches whose Pacific workday falls inside [from, to], closed ones first. */
export function punchesForWeek(punches: Punch[], from: string, to: string, zone = PACIFIC): Punch[] {
  return punches
    .filter((p) => { const d = wallClock(p.clock_in, zone).date; return d >= from && d <= to; })
    .sort((a, b) => Date.parse(a.clock_in) - Date.parse(b.clock_in));
}

export function buildExport(punches: Punch[], names: Names, zone = PACIFIC): Export {
  const rows: ExportRow[] = [];
  const out: ExportPunch[] = [];
  const stillOpen: string[] = [];

  for (const p of punches) {
    const who = nameOf(names, p.technician_id);
    if (!p.clock_out) { stillOpen.push(who); continue; }
    const end = p.clock_out;
    const breaks = [...(p.breaks ?? [])].sort(byStart)
      .map((b) => ({ started_at: b.started_at, ended_at: b.ended_at ?? end }));

    const cin = wallClock(p.clock_in, zone);
    const cout = wallClock(end, zone);
    const meal = (i: number) => {
      const b = breaks[i];
      return b ? { s: wallClock(b.started_at, zone).time, e: wallClock(b.ended_at, zone).time } : { s: "", e: "" };
    };
    const notes: string[] = [];
    if (cout.date !== cin.date) notes.push(`clocked out ${cout.date}`);
    if (breaks.length > 2) {
      const extra = breaks.slice(2).map((b) => `${wallClock(b.started_at, zone).time}-${wallClock(b.ended_at, zone).time}`);
      notes.push(`${breaks.length} breaks punched, two fit the columns, also ${extra.join(" and ")}`);
    }

    rows.push({
      employee: who, date: cin.date, clock_in: cin.time, clock_out: cout.time,
      meal_start: meal(0).s, meal_end: meal(0).e,
      rest_breaks_taken: "",
      meal2_start: meal(1).s, meal2_end: meal(1).e,
      note: notes.join(". "),
    });
    out.push({ technician: who, clock_in: p.clock_in, clock_out: end, breaks });
  }
  return { rows, punches: out, stillOpen: [...new Set(stillOpen)] };
}

const cell = (v: string): string => (/[",\r\n]/.test(v) ? `"${v.replace(/"/g, "\"\"")}"` : v);

/** The calculator's CSV. Header first, LF line ends, quoted only when needed. */
export function toCsv(rows: ExportRow[]): string {
  const lines = [CSV_COLUMNS.join(",")];
  for (const r of rows) lines.push(CSV_COLUMNS.map((c) => cell(r[c])).join(","));
  return lines.join("\n") + "\n";
}

/** The calculator's JSON: {"punches": [...]}, timestamps exactly as stored. */
export function toJson(punches: ExportPunch[], from: string, to: string): string {
  return JSON.stringify({
    _comment: `Shop Desk pay_clock export for the workweek ${from} to ${to}. Timestamps are UTC; the workday is the Pacific day the shift started.`,
    punches,
  }, null, 2) + "\n";
}
